<template>
  <!-- Beneficiary Modal -->
  <div class="modal fade" id="beneficiaryModal" tabindex="-1" aria-labelledby="beneficiaryModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="beneficiaryModalLabel">
            {{ modalTitle }}
          </h5>
          <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <!-- Form Content -->
          <div v-else>
            <!-- Alert Messages -->
            <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
              {{ message }}
              <button type="button" class="btn-close" @click="clearAlerts"></button>
            </div>
            <div v-if="alert.failed" class="alert alert-danger alert-dismissible fade show" role="alert">
              {{ message }}
              <button type="button" class="btn-close" @click="clearAlerts"></button>
            </div>

            <!-- Spouse Limit Warning -->
            <div v-if="hasSpouseWarning" class="alert alert-warning">
              <i class="bx bx-info-circle me-2"></i>
              <strong>Note:</strong> Only one spouse is allowed per primary member. A spouse has already been registered.
            </div>

            <!-- Beneficiary Form -->
            <form @submit.prevent="saveBeneficiary">
              <div class="row">
                <!-- Full Name -->
                <div class="col-md-12 mb-3">
                  <label for="fullName" class="form-label">Full Name <span class="text-danger">*</span></label>
                  <input type="text"
                         class="form-control"
                         id="fullName"
                         v-model="beneficiaryForm.fullName"
                         placeholder="Enter beneficiary's full name"
                         required>
                  <div v-if="errors.fullName" class="text-danger small mt-1">
                    {{ errors.fullName }}
                  </div>
                </div>

                <!-- Gender -->
                <div class="col-md-6 mb-3">
                  <label for="gender" class="form-label">Gender <span class="text-danger">*</span></label>
                  <select class="form-select" id="gender" v-model="beneficiaryForm.gender" required>
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  <div v-if="errors.gender" class="text-danger small mt-1">
                    {{ errors.gender }}
                  </div>
                </div>

                <!-- Date of Birth -->
                <div class="col-md-6 mb-3">
                  <label for="dateOfBirth" class="form-label">Date of Birth <span class="text-danger">*</span></label>
                  <input type="date"
                         class="form-control"
                         id="dateOfBirth"
                         v-model="beneficiaryForm.dateOfBirth"
                         :max="maxDate"
                         :min="minDate"
                         @change="onDateOfBirthChange"
                         required>
                  <div v-if="errors.dateOfBirth" class="text-danger small mt-1">
                    {{ errors.dateOfBirth }}
                  </div>
                  <small class="text-muted" v-if="beneficiaryAge !== null">
                    Age: {{ beneficiaryAge }} years
                    <span v-if="relationshipAgeWarning && !isAdultChildWithSpecialCase" class="text-danger d-block">
                      {{ relationshipAgeWarning }}
                    </span>
                  </small>
                </div>

                <!-- Relationship -->
                <div class="col-md-6 mb-3">
                  <label for="relationship" class="form-label">Relationship <span class="text-danger">*</span></label>
                  <select class="form-select"
                          id="relationship"
                          v-model="beneficiaryForm.relationship"
                          @change="onRelationshipChange"
                          :class="{'is-invalid': errors.relationship}"
                          required>
                    <option value="">Select Relationship</option>
                    <option value="spouse" :disabled="shouldDisableSpouse">
                      Spouse {{ shouldDisableSpouse ? '(Already registered - limit reached)' : '' }}
                    </option>
                    <option value="child">Child</option>
                    <option value="parent">Parent</option>
                    <option value="sibling">Sibling</option>
                    <option value="grandparent">Grandparent</option>
                    <option value="grandchild">Grandchild</option>
                    <option value="other">Other</option>
                  </select>
                  <div v-if="errors.relationship" class="text-danger small mt-1">
                    {{ errors.relationship }}
                  </div>
                  <small class="text-info d-block" v-if="ageRequirementText">
                    <i class="bx bx-info-circle"></i> {{ ageRequirementText }}
                  </small>
                </div>

                <!-- ID Number -->
                <div class="col-md-6 mb-3">
                  <label for="idNumber" class="form-label">ID Number</label>
                  <input type="text"
                         class="form-control"
                         id="idNumber"
                         v-model="beneficiaryForm.idNumber"
                         placeholder="Enter ID number (optional)">
                </div>

                <!-- Phone Number -->
                <div class="col-md-6 mb-3">
                  <label for="phoneNumber" class="form-label">Phone Number</label>
                  <input type="tel"
                         class="form-control"
                         id="phoneNumber"
                         v-model="beneficiaryForm.phoneNumber"
                         placeholder="Enter phone number (optional)">
                </div>

                <!-- Blood Group -->
                <div class="col-md-6 mb-3">
                  <label for="bloodGroup" class="form-label">Blood Group</label>
                  <select class="form-select" id="bloodGroup" v-model="beneficiaryForm.bloodGroup">
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>

                <!-- Allergies -->
                <div class="col-md-6 mb-3">
                  <label for="allergies" class="form-label">Allergies</label>
                  <input type="text"
                         class="form-control"
                         id="allergies"
                         v-model="beneficiaryForm.allergies"
                         placeholder="Enter any allergies (optional)">
                </div>

                <!-- Chronic Conditions -->
                <div class="col-md-6 mb-3">
                  <label for="chronicConditions" class="form-label">Chronic Conditions</label>
                  <input type="text"
                         class="form-control"
                         id="chronicConditions"
                         v-model="beneficiaryForm.chronicConditions"
                         placeholder="Enter any chronic conditions (optional)">
                </div>

                <!-- ✅ NEW: Special Cases Section for Adult Children -->
                <div class="col-12" v-if="showSpecialCasesSection">
                  <hr class="my-3">
                  <div class="alert alert-info py-2 mb-3">
                    <div class="d-flex align-items-center">
                      <i class="bx bx-info-circle fs-4 me-2"></i>
                      <div>
                        <strong>Adult Child ({{ beneficiaryAge }} years old)</strong>
                        <p class="mb-0 small">
                          This child is {{ beneficiaryAge }} years old, which is above the free coverage threshold of {{ adultChildAgeThreshold }} years.
                          However, adult children with <strong>disabilities</strong> or who are <strong>dependents (no income)</strong> may qualify for FREE coverage.
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Disability Section -->
                  <div class="card border-danger mb-3" :class="{'border-2': beneficiaryForm.hasDisability}">
                    <div class="card-header bg-danger bg-opacity-10 py-2">
                      <div class="form-check mb-0">
                        <input class="form-check-input"
                               type="checkbox"
                               id="hasDisability"
                               v-model="beneficiaryForm.hasDisability"
                               @change="onDisabilityChange">
                        <label class="form-check-label fw-bold" for="hasDisability">
                          <i class="bx bx-accessibility me-1"></i>
                          Has Disability (Mental or Physical)
                        </label>
                      </div>
                    </div>
                    <div class="card-body py-2" v-if="beneficiaryForm.hasDisability">
                      <label for="disabilityNotes" class="form-label small mb-1">
                        Disability Details <span class="text-danger">*</span>
                      </label>
                      <textarea class="form-control"
                                id="disabilityNotes"
                                v-model="beneficiaryForm.disabilityNotes"
                                rows="2"
                                placeholder="Describe the disability (e.g., visual impairment, mobility issues, mental health condition)"
                                :class="{'is-invalid': errors.disabilityNotes}"
                                required></textarea>
                      <div v-if="errors.disabilityNotes" class="text-danger small mt-1">
                        {{ errors.disabilityNotes }}
                      </div>
                      <small class="text-success d-block mt-1">
                        <i class="bx bx-check-circle me-1"></i>
                        This beneficiary will qualify for FREE coverage due to disability.
                      </small>
                    </div>
                  </div>

                  <!-- Dependency Section -->
                  <div class="card border-purple mb-3" :class="{'border-2': beneficiaryForm.isDependent}" v-if="!beneficiaryForm.hasDisability">
                    <div class="card-header bg-purple bg-opacity-10 py-2">
                      <div class="form-check mb-0">
                        <input class="form-check-input"
                               type="checkbox"
                               id="isDependent"
                               v-model="beneficiaryForm.isDependent"
                               @change="onDependencyChange">
                        <label class="form-check-label fw-bold" for="isDependent">
                          <i class="bx bx-home-heart me-1"></i>
                          Is Dependent (No Income / Lives at Home)
                        </label>
                      </div>
                    </div>
                    <div class="card-body py-2" v-if="beneficiaryForm.isDependent">
                      <label for="dependencyNotes" class="form-label small mb-1">
                        Dependency Details <span class="text-danger">*</span>
                      </label>
                      <textarea class="form-control"
                                id="dependencyNotes"
                                v-model="beneficiaryForm.dependencyNotes"
                                rows="2"
                                placeholder="Explain the dependency situation (e.g., student, unemployed, caring for family)"
                                :class="{'is-invalid': errors.dependencyNotes}"
                                required></textarea>
                      <div v-if="errors.dependencyNotes" class="text-danger small mt-1">
                        {{ errors.dependencyNotes }}
                      </div>
                      <small class="text-success d-block mt-1">
                        <i class="bx bx-check-circle me-1"></i>
                        This beneficiary will qualify for FREE coverage as a dependent.
                      </small>
                    </div>
                  </div>

                  <!-- Warning if no special case selected -->
                  <div class="alert alert-warning py-2" v-if="!beneficiaryForm.hasDisability && !beneficiaryForm.isDependent">
                    <i class="bx bx-error-circle me-1"></i>
                    <strong>Note:</strong> Without disability or dependency status, this adult child will be charged
                    <strong>ETB {{ formatCurrency(additionalMemberFee) }}</strong> ({{ additionalMemberRatePercent }}% of primary amount).
                  </div>
                </div>

                <!-- Notes -->
                <div class="col-md-12 mb-3">
                  <label for="notes" class="form-label">Notes</label>
                  <textarea class="form-control"
                            id="notes"
                            v-model="beneficiaryForm.notes"
                            rows="2"
                            placeholder="Enter any additional notes (optional)"></textarea>
                </div>

                <!-- Status (only show for edit) -->
                <div v-if="isEditMode" class="col-md-6 mb-3">
                  <label for="status" class="form-label">Status</label>
                  <select class="form-select" id="status" v-model="beneficiaryForm.status">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <!-- Coverage Information -->
              <div v-if="!isEditMode && memberInfo.enrollment_type === 'cbhi_member'" class="alert alert-info mt-3">
                <h6 class="alert-heading">
                  <i class="bx bx-info-circle me-2"></i>Coverage & Premium Information:
                </h6>
                <div v-if="planRules" class="mb-2">
                  <strong>Plan Rules:</strong>
                  <ul class="mb-2 mt-1 small">
                    <li>Free Spouses: {{ planRules.free_spouses_count === 999 ? 'Unlimited' : planRules.free_spouses_count }}</li>
                    <li>Free Children Under: {{ planRules.free_children_under_18 === 1 ? planRules.adult_child_age_threshold + ' years' : 'Not applicable' }}</li>
                    <li>Additional Member Rate: {{ (planRules.additional_member_rate * 100).toFixed(0) }}%</li>
                    <li class="text-primary"><strong>Adult children with disability or dependency: FREE</strong></li>
                  </ul>
                </div>
                <ul class="mb-0 mt-2">
                  <li>
                    <strong>Current beneficiaries:</strong> {{ currentBeneficiaryCount }}
                  </li>
                  <li v-if="willNewBeneficiaryBeCharged">
                    <strong class="text-warning">This beneficiary will be charged</strong>
                    <span class="d-block small">{{ chargeReason }}</span>
                  </li>
                  <li v-else>
                    <strong class="text-success">This beneficiary will be FREE</strong>
                    <span class="d-block small">{{ freeReason }}</span>
                  </li>
                </ul>
              </div>

              <!-- Premium Breakdown for Edit Mode -->
              <div v-if="isEditMode && memberInfo.enrollment_type === 'cbhi_member'" class="alert alert-secondary mt-3">
                <h6 class="alert-heading">
                  <i class="bx bx-calculator me-2"></i>Current Coverage:
                </h6>
                <ul class="mb-0 mt-2 small">
                  <li>Total Beneficiaries: {{ currentBeneficiaryCount }}</li>
                  <li v-if="memberInfo.annual_premium">Current Annual Premium: <strong>ETB {{ formatCurrency(memberInfo.annual_premium) }}</strong></li>
                  <li v-if="beneficiaryForm.hasDisability || beneficiaryForm.isDependent">
                    <span class="text-success">
                      <i class="bx bx-check-circle me-1"></i>
                      Special Case: {{ beneficiaryForm.hasDisability ? 'Disability' : 'Dependent' }} - FREE Coverage
                    </span>
                  </li>
                </ul>
              </div>
            </form>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            <i class="bx bx-x me-1"></i> Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="saveBeneficiary" :disabled="loading || !isFormValid">
            <span v-if="loading">
              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </span>
            <span v-else>
              <i class="bx bx-save me-1"></i> {{ isEditMode ? 'Update' : 'Add' }} Beneficiary
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <div class="modal fade" id="deleteBeneficiaryModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header bg-danger text-white">
          <h5 class="modal-title">
            <i class="bx bx-trash me-2"></i>Remove Beneficiary
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeDeleteModal"></button>
        </div>
        <div class="modal-body">
          <div class="text-center mb-4">
            <i class="bx bx-error-circle display-1 text-danger"></i>
          </div>
          <p class="text-center">
            Are you sure you want to remove <strong>{{ beneficiaryToDelete?.fullName }}</strong>
            from coverage?
          </p>

          <!-- Show special case info if applicable -->
          <div v-if="beneficiaryToDelete?.hasDisability" class="alert alert-info py-2 mb-3">
            <i class="bx bx-accessibility me-1"></i>
            <strong>Note:</strong> This beneficiary has disability status and is receiving FREE coverage.
          </div>
          <div v-if="beneficiaryToDelete?.isDependent && !beneficiaryToDelete?.hasDisability" class="alert alert-info py-2 mb-3">
            <i class="bx bx-home-heart me-1"></i>
            <strong>Note:</strong> This beneficiary has dependent status and is receiving FREE coverage.
          </div>

          <div class="alert alert-warning">
            <i class="bx bx-info-circle me-2"></i>
            <strong>Warning:</strong> This action will:
            <ul class="mb-0 mt-2">
              <li>Remove the beneficiary from insurance coverage</li>
              <li>Recalculate the annual premium based on remaining beneficiaries</li>
              <li>Archive all related claims history</li>
            </ul>
          </div>
          <div v-if="memberInfo.annual_premium" class="text-muted small mt-3">
            <strong>Current Premium:</strong>
            <ul class="mb-0">
              <li>Total Beneficiaries: {{ currentBeneficiaryCount }}</li>
              <li>Current Annual Premium: ETB {{ formatCurrency(memberInfo.annual_premium) }}</li>
            </ul>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeDeleteModal">
            <i class="bx bx-x me-1"></i> Cancel
          </button>
          <button type="button" class="btn btn-danger" @click="confirmDelete" :disabled="deleting">
            <span v-if="deleting">
              <span class="spinner-border spinner-border-sm me-2" role="status"></span>
              Removing...
            </span>
            <span v-else>
              <i class="bx bx-trash me-1"></i> Remove Beneficiary
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: 'BeneficiaryModal',
  props: {
    cbhiNumber: {
      type: String,
      required: true
    },
    currentBeneficiaryCount: {
      type: Number,
      default: 0
    },
    memberInfo: {
      type: Object,
      default: () => ({})
    },
    existingBeneficiaries: {
      type: Array,
      default: () => []
    },
    selectedPlan: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      // Modal state
      modalInstance: null,
      deleteModalInstance: null,
      isEditMode: false,
      loading: false,
      deleting: false,

      // Form data
      beneficiaryForm: {
        beneficiaryId: null,
        fullName: '',
        gender: '',
        dateOfBirth: '',
        relationship: '',
        idNumber: '',
        phoneNumber: '',
        bloodGroup: '',
        allergies: '',
        chronicConditions: '',
        notes: '',
        status: 'active',
        // ✅ NEW: Special case fields
        hasDisability: false,
        disabilityNotes: '',
        isDependent: false,
        dependencyNotes: ''
      },

      // Beneficiary to delete
      beneficiaryToDelete: null,

      // Validation errors
      errors: {},

      // Alert states
      alert: {
        success: false,
        failed: false
      },
      message: '',

      // Plan rules loaded from memberInfo or selectedPlan
      planRules: null
    };
  },

  computed: {
    modalTitle() {
      return this.isEditMode ? 'Edit Beneficiary' : 'Add New Beneficiary';
    },

    hasSpouseWarning() {
      return !this.isEditMode && this.hasExistingSpouse && this.beneficiaryForm.relationship !== 'spouse';
    },

    hasExistingSpouse() {
      return this.existingBeneficiaries.some(b =>
          ['spouse', 'husband', 'wife'].includes((b.relationship || '').toLowerCase()) &&
          b.status === 'active'
      );
    },

    shouldDisableSpouse() {
      // Check plan rules for spouse limit
      if (this.planRules && this.planRules.free_spouses_count === 999) {
        return false; // Unlimited spouses allowed
      }

      // Count existing spouses
      const existingSpouseCount = this.existingBeneficiaries.filter(
          b => ['spouse', 'husband', 'wife'].includes((b.relationship || '').toLowerCase()) &&
              b.status === 'active'
      ).length;

      // In edit mode, check if this is not the current spouse being edited
      if (this.isEditMode) {
        const currentIsSpouse = ['spouse', 'husband', 'wife'].includes((this.beneficiaryForm.relationship || '').toLowerCase());
        if (currentIsSpouse) {
          return false;
        }
        const maxSpouses = this.planRules?.free_spouses_count || 1;
        return existingSpouseCount >= maxSpouses;
      }

      // In add mode, check against max allowed
      const maxSpouses = this.planRules?.free_spouses_count || 1;
      return existingSpouseCount >= maxSpouses;
    },

    maxDate() {
      return new Date().toISOString().split('T')[0];
    },

    minDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 120);
      return date.toISOString().split('T')[0];
    },

    beneficiaryAge() {
      if (!this.beneficiaryForm.dateOfBirth) return null;
      return this.calculateAge(this.beneficiaryForm.dateOfBirth);
    },

    // ✅ NEW: Get adult child age threshold from plan
    adultChildAgeThreshold() {
      return this.planRules?.adult_child_age_threshold ||
          this.selectedPlan?.adult_child_age_threshold || 18;
    },

    // ✅ NEW: Get additional member fee
    additionalMemberFee() {
      const primaryAmount = this.planRules?.primary_annual_amount ||
          this.selectedPlan?.primary_annual_amount || 0;
      const rate = this.planRules?.additional_member_rate ||
          this.selectedPlan?.additional_member_rate || 0.20;
      return primaryAmount * rate;
    },

    // ✅ NEW: Get additional member rate as percentage
    additionalMemberRatePercent() {
      const rate = this.planRules?.additional_member_rate ||
          this.selectedPlan?.additional_member_rate || 0.20;
      return (rate * 100).toFixed(0);
    },

    isChildRelationship() {
      const childRelationships = ['child', 'son', 'daughter', 'grandchild'];
      return childRelationships.includes((this.beneficiaryForm.relationship || '').toLowerCase());
    },

    isAdultRelationship() {
      const adultRelationships = ['spouse', 'husband', 'wife', 'parent', 'grandparent'];
      return adultRelationships.includes((this.beneficiaryForm.relationship || '').toLowerCase());
    },

    // ✅ NEW: Check if this is an adult child (child relationship but over threshold)
    isAdultChild() {
      if (!this.isChildRelationship) return false;
      if (this.beneficiaryAge === null) return false;
      return this.beneficiaryAge >= this.adultChildAgeThreshold;
    },

    // ✅ NEW: Check if adult child has special case (disability or dependency)
    isAdultChildWithSpecialCase() {
      if (!this.isAdultChild) return false;
      return this.beneficiaryForm.hasDisability || this.beneficiaryForm.isDependent;
    },

    // ✅ NEW: Show special cases section for adult children
    showSpecialCasesSection() {
      return this.isAdultChild;
    },

    relationshipAgeWarning() {
      if (!this.beneficiaryAge || !this.beneficiaryForm.relationship) return '';

      // For adult children with special cases, no warning
      if (this.isAdultChildWithSpecialCase) return '';

      if (this.isChildRelationship && this.beneficiaryAge >= this.adultChildAgeThreshold) {
        return `Child is ${this.beneficiaryAge} years old (threshold: ${this.adultChildAgeThreshold}). Check disability/dependency below for free coverage, otherwise additional charges apply.`;
      }

      if (this.isAdultRelationship && this.beneficiaryAge < 18) {
        return `Must be 18 or older for ${this.beneficiaryForm.relationship}`;
      }

      return '';
    },

    ageRequirementText() {
      if (!this.beneficiaryForm.relationship) return '';

      if (this.isChildRelationship) {
        return `Free if under ${this.adultChildAgeThreshold} years, or if adult child has disability/dependency`;
      }

      if (this.isAdultRelationship) {
        return 'Must be 18 years or older';
      }

      return 'Any age allowed for this relationship';
    },

    // ✅ UPDATED: Check if beneficiary will be charged
    willNewBeneficiaryBeCharged() {
      if (!this.beneficiaryForm.relationship || this.isEditMode) return false;

      const relationship = (this.beneficiaryForm.relationship || '').toLowerCase();
      const age = this.beneficiaryAge || 0;

      // Check if it's a spouse beyond free limit
      if (['spouse', 'husband', 'wife'].includes(relationship)) {
        const existingSpouseCount = this.existingBeneficiaries.filter(
            b => ['spouse', 'husband', 'wife'].includes((b.relationship || '').toLowerCase()) &&
                b.status === 'active'
        ).length;
        const freeSpousesCount = this.planRules?.free_spouses_count || 1;

        if (freeSpousesCount !== 999 && existingSpouseCount >= freeSpousesCount) {
          return true;
        }
        return false; // Free spouse
      }

      // Check if it's a child
      if (['child', 'son', 'daughter'].includes(relationship)) {
        // Child under threshold - FREE
        if (age < this.adultChildAgeThreshold) {
          return false;
        }
        // Adult child with disability - FREE
        if (this.beneficiaryForm.hasDisability) {
          return false;
        }
        // Adult child with dependency - FREE
        if (this.beneficiaryForm.isDependent) {
          return false;
        }
        // Adult child without special case - CHARGED
        return true;
      }

      // Other relatives are always charged
      if (['parent', 'sibling', 'grandparent', 'grandchild', 'other'].includes(relationship)) {
        return true;
      }

      return false;
    },

    // ✅ NEW: Reason why beneficiary is free
    freeReason() {
      if (!this.beneficiaryForm.relationship) return '';

      const relationship = (this.beneficiaryForm.relationship || '').toLowerCase();
      const age = this.beneficiaryAge || 0;

      if (['spouse', 'husband', 'wife'].includes(relationship)) {
        return 'Covered as spouse under plan rules';
      }

      if (['child', 'son', 'daughter'].includes(relationship)) {
        if (age < this.adultChildAgeThreshold) {
          return `Child under ${this.adultChildAgeThreshold} years - covered free`;
        }
        if (this.beneficiaryForm.hasDisability) {
          return 'Adult child with disability - covered free';
        }
        if (this.beneficiaryForm.isDependent) {
          return 'Adult child who is dependent - covered free';
        }
      }

      return 'Covered under plan rules';
    },

    chargeReason() {
      if (!this.willNewBeneficiaryBeCharged) return '';

      const relationship = (this.beneficiaryForm.relationship || '').toLowerCase();
      const age = this.beneficiaryAge || 0;
      const chargeAmount = this.additionalMemberFee;

      if (['spouse', 'husband', 'wife'].includes(relationship)) {
        return `Additional spouse (beyond free limit): ETB ${this.formatCurrency(chargeAmount)}`;
      }

      if (['child', 'son', 'daughter'].includes(relationship)) {
        return `Adult child (${age} years, no disability/dependency): ETB ${this.formatCurrency(chargeAmount)}`;
      }

      return `${this.capitalizeFirst(relationship)}: ETB ${this.formatCurrency(chargeAmount)}`;
    },

    // ✅ UPDATED: Form validation including special case fields
    isFormValid() {
      // Basic required fields
      if (!this.beneficiaryForm.fullName.trim()) return false;
      if (!this.beneficiaryForm.gender) return false;
      if (!this.beneficiaryForm.relationship) return false;
      if (!this.beneficiaryForm.dateOfBirth) return false;

      // Age validation for adult relationships
      if (this.isAdultRelationship && this.beneficiaryAge < 18) return false;

      // Disability notes required if disability is checked
      if (this.beneficiaryForm.hasDisability && !this.beneficiaryForm.disabilityNotes.trim()) {
        return false;
      }

      // Dependency notes required if dependency is checked
      if (this.beneficiaryForm.isDependent && !this.beneficiaryForm.dependencyNotes.trim()) {
        return false;
      }

      return true;
    }
  },

  mounted() {
    if (typeof window !== 'undefined' && window.bootstrap) {
      this.modalInstance = new window.bootstrap.Modal(document.getElementById('beneficiaryModal'));
      this.deleteModalInstance = new window.bootstrap.Modal(document.getElementById('deleteBeneficiaryModal'));
    }

    this.loadPlanRules();
  },

  watch: {
    memberInfo: {
      handler() {
        this.loadPlanRules();
      },
      deep: true
    },
    selectedPlan: {
      handler() {
        this.loadPlanRules();
      },
      deep: true
    }
  },

  methods: {
    loadPlanRules() {
      // Extract plan rules from selectedPlan prop first, then memberInfo
      if (this.selectedPlan) {
        this.planRules = {
          free_spouses_count: this.selectedPlan.free_spouses_count || 1,
          free_children_under_18: this.selectedPlan.free_children_under_18 || 1,
          adult_child_age_threshold: this.selectedPlan.adult_child_age_threshold || 18,
          additional_member_rate: this.selectedPlan.additional_member_rate || 0.20,
          primary_annual_amount: this.selectedPlan.primary_annual_amount || 0
        };
      } else if (this.memberInfo && this.memberInfo.enrollmentType === 'cbhi_member') {
        this.planRules = {
          free_spouses_count: this.memberInfo.free_spouses_count || 1,
          free_children_under_18: this.memberInfo.free_children_under_18 || 1,
          adult_child_age_threshold: this.memberInfo.adult_child_age_threshold || 18,
          additional_member_rate: this.memberInfo.additional_member_rate || 0.20,
          primary_annual_amount: this.memberInfo.primary_annual_amount || 0
        };
      }
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    openAddModal() {
      this.resetForm();
      this.isEditMode = false;
      this.clearAlerts();
      this.loadPlanRules();
      this.modalInstance.show();
    },

    openEditModal(beneficiary) {
      this.resetForm();
      this.isEditMode = true;
      this.clearAlerts();
      this.loadPlanRules();

      this.beneficiaryForm = {
        beneficiaryId: beneficiary.beneficiary_id || beneficiary.beneficiaryId,
        fullName: beneficiary.full_name || beneficiary.fullName || '',
        gender: beneficiary.gender || '',
        dateOfBirth: beneficiary.date_of_birth || beneficiary.dateOfBirth || '',
        relationship: beneficiary.relationship || '',
        idNumber: beneficiary.id_number || beneficiary.idNumber || '',
        phoneNumber: beneficiary.phone_number || beneficiary.phoneNumber || '',
        bloodGroup: beneficiary.blood_group || beneficiary.bloodGroup || '',
        allergies: beneficiary.allergies || '',
        chronicConditions: beneficiary.chronic_conditions || beneficiary.chronicConditions || '',
        notes: beneficiary.notes || '',
        status: beneficiary.status || 'active',
        // ✅ NEW: Map special case fields
        hasDisability: beneficiary.hasDisability || beneficiary.has_disability || false,
        disabilityNotes: beneficiary.disabilityNotes || beneficiary.disability_notes || '',
        isDependent: beneficiary.isDependent || beneficiary.is_dependent || false,
        dependencyNotes: beneficiary.dependencyNotes || beneficiary.dependency_notes || ''
      };

      this.modalInstance.show();
    },

    openDeleteModal(beneficiary) {
      this.beneficiaryToDelete = {
        id: beneficiary.beneficiary_id || beneficiary.beneficiaryId,
        fullName: beneficiary.full_name || beneficiary.fullName,
        hasDisability: beneficiary.hasDisability || beneficiary.has_disability || false,
        isDependent: beneficiary.isDependent || beneficiary.is_dependent || false
      };
      this.deleteModalInstance.show();
    },

    closeModal() {
      this.modalInstance.hide();
      this.resetForm();
      this.clearAlerts();
    },

    closeDeleteModal() {
      this.deleteModalInstance.hide();
      this.beneficiaryToDelete = null;
    },

    resetForm() {
      this.beneficiaryForm = {
        beneficiaryId: null,
        fullName: '',
        gender: '',
        dateOfBirth: '',
        relationship: '',
        idNumber: '',
        phoneNumber: '',
        bloodGroup: '',
        allergies: '',
        chronicConditions: '',
        notes: '',
        status: 'active',
        // ✅ NEW: Reset special case fields
        hasDisability: false,
        disabilityNotes: '',
        isDependent: false,
        dependencyNotes: ''
      };
      this.errors = {};
    },

    clearAlerts() {
      this.alert.success = false;
      this.alert.failed = false;
      this.message = '';
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success') {
        setTimeout(() => {
          this.clearAlerts();
        }, 3000);
      }
    },

    // ✅ NEW: Handle date of birth change
    onDateOfBirthChange() {
      // Reset special case fields if age changes and no longer adult child
      if (!this.isAdultChild) {
        this.beneficiaryForm.hasDisability = false;
        this.beneficiaryForm.disabilityNotes = '';
        this.beneficiaryForm.isDependent = false;
        this.beneficiaryForm.dependencyNotes = '';
      }
    },

    // ✅ NEW: Handle disability checkbox change
    onDisabilityChange() {
      if (this.beneficiaryForm.hasDisability) {
        // If disability is checked, uncheck dependency
        this.beneficiaryForm.isDependent = false;
        this.beneficiaryForm.dependencyNotes = '';
      } else {
        this.beneficiaryForm.disabilityNotes = '';
      }
      // Clear any previous errors
      delete this.errors.disabilityNotes;
    },

    // ✅ NEW: Handle dependency checkbox change
    onDependencyChange() {
      if (!this.beneficiaryForm.isDependent) {
        this.beneficiaryForm.dependencyNotes = '';
      }
      // Clear any previous errors
      delete this.errors.dependencyNotes;
    },

    // ✅ UPDATED: Validate form including special case fields
    validateForm() {
      this.errors = {};
      let isValid = true;

      if (!this.beneficiaryForm.fullName.trim()) {
        this.errors.fullName = 'Full name is required';
        isValid = false;
      }

      if (!this.beneficiaryForm.gender) {
        this.errors.gender = 'Gender is required';
        isValid = false;
      }

      if (!this.beneficiaryForm.dateOfBirth) {
        this.errors.dateOfBirth = 'Date of birth is required';
        isValid = false;
      }

      if (!this.beneficiaryForm.relationship) {
        this.errors.relationship = 'Relationship is required';
        isValid = false;
      }

      // Check spouse limit
      if (['spouse', 'husband', 'wife'].includes((this.beneficiaryForm.relationship || '').toLowerCase()) &&
          this.shouldDisableSpouse) {
        this.errors.relationship = 'Spouse limit reached for this plan';
        isValid = false;
      }

      // Validate age based on relationship
      if (this.beneficiaryForm.dateOfBirth && this.beneficiaryForm.relationship) {
        const age = this.calculateAge(this.beneficiaryForm.dateOfBirth);

        if (age < 0) {
          this.errors.dateOfBirth = 'Invalid date of birth';
          isValid = false;
        } else if (this.isAdultRelationship && age < 18) {
          this.errors.dateOfBirth = `${this.capitalizeFirst(this.beneficiaryForm.relationship)} must be 18 years or older. Current age: ${age} years`;
          isValid = false;
        }
        // Note: We no longer block adult children - they can be added with charges or special cases
      }

      // ✅ NEW: Validate disability notes if disability is checked
      if (this.beneficiaryForm.hasDisability && !this.beneficiaryForm.disabilityNotes.trim()) {
        this.errors.disabilityNotes = 'Please provide disability details';
        isValid = false;
      }

      // ✅ NEW: Validate dependency notes if dependency is checked
      if (this.beneficiaryForm.isDependent && !this.beneficiaryForm.dependencyNotes.trim()) {
        this.errors.dependencyNotes = 'Please provide dependency details';
        isValid = false;
      }

      return isValid;
    },

    onRelationshipChange() {
      // Clear relationship error
      if (this.errors.relationship) {
        delete this.errors.relationship;
      }

      // Check if trying to select spouse when limit reached
      if (['spouse', 'husband', 'wife'].includes((this.beneficiaryForm.relationship || '').toLowerCase()) &&
          this.shouldDisableSpouse) {
        this.errors.relationship = 'Spouse limit reached for this plan.';
        this.beneficiaryForm.relationship = '';
        return;
      }

      // Reset special case fields if relationship changes to non-child
      if (!this.isChildRelationship) {
        this.beneficiaryForm.hasDisability = false;
        this.beneficiaryForm.disabilityNotes = '';
        this.beneficiaryForm.isDependent = false;
        this.beneficiaryForm.dependencyNotes = '';
      }

      // Clear date of birth error when relationship changes
      if (this.errors.dateOfBirth) {
        delete this.errors.dateOfBirth;
      }
    },

    // ✅ UPDATED: Save beneficiary with special case fields
    async saveBeneficiary() {
      if (!this.validateForm()) {
        return;
      }

      this.loading = true;
      this.clearAlerts();

      const data = {
        function: this.isEditMode ? 'updateBeneficiary' : 'addBeneficiary',
        cbhiNumber: this.cbhiNumber,
        fullName: this.beneficiaryForm.fullName.trim(),
        gender: this.beneficiaryForm.gender,
        dateOfBirth: this.beneficiaryForm.dateOfBirth,
        relationship: this.beneficiaryForm.relationship,
        idNumber: this.beneficiaryForm.idNumber.trim() || null,
        phoneNumber: this.beneficiaryForm.phoneNumber.trim() || null,
        bloodGroup: this.beneficiaryForm.bloodGroup || null,
        allergies: this.beneficiaryForm.allergies.trim() || null,
        chronicConditions: this.beneficiaryForm.chronicConditions.trim() || null,
        notes: this.beneficiaryForm.notes.trim() || null,
        // ✅ NEW: Include special case fields
        hasDisability: this.beneficiaryForm.hasDisability || false,
        disabilityNotes: this.beneficiaryForm.disabilityNotes.trim() || null,
        isDependent: this.beneficiaryForm.isDependent || false,
        dependencyNotes: this.beneficiaryForm.dependencyNotes.trim() || null
      };

      // Add beneficiaryId for update
      if (this.isEditMode) {
        data.beneficiaryId = this.beneficiaryForm.beneficiaryId;
        data.status = this.beneficiaryForm.status;
      }

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.showAlert('success', res.data.message);

          this.$emit('beneficiary-saved', {
            action: this.isEditMode ? 'updated' : 'added',
            beneficiary: res.data.data
          });

          setTimeout(() => {
            this.closeModal();
          }, 1500);
        } else {
          this.showAlert('failed', res.data.message || 'Failed to save beneficiary');
        }
      } catch (error) {
        console.error('Error saving beneficiary:', error);
        this.showAlert('failed', error.message || 'An error occurred while saving beneficiary');
      } finally {
        this.loading = false;
      }
    },

    async confirmDelete() {
      if (!this.beneficiaryToDelete) return;

      this.deleting = true;

      const data = {
        function: 'removeBeneficiary',
        cbhiNumber: this.cbhiNumber,
        beneficiaryId: this.beneficiaryToDelete.id,
        reason: 'Removed by user from member details page'
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('beneficiary-removed', {
            beneficiaryId: this.beneficiaryToDelete.id,
            data: res.data.data
          });

          this.closeDeleteModal();
        } else {
          alert(res.data.message || 'Failed to remove beneficiary');
        }
      } catch (error) {
        console.error('Error removing beneficiary:', error);
        alert(error.message || 'An error occurred while removing beneficiary');
      } finally {
        this.deleting = false;
      }
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return 0;
      const dob = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      return age;
    }
  }
};
</script>

<style scoped>
.modal-header {
  border-bottom: 1px solid #e9ecef;
}

.modal-footer {
  border-top: 1px solid #e9ecef;
}

.form-label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.5rem;
}

.text-danger {
  color: #dc3545 !important;
}

.text-success {
  color: #28a745 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-info {
  color: #17a2b8 !important;
}

.text-primary {
  color: #2c5aa0 !important;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}

.alert {
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: 0.25rem;
}

.alert-heading {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.alert-info {
  color: #004085;
  background-color: #d1ecf1;
  border-color: #bee5eb;
}

.alert-warning {
  color: #856404;
  background-color: #fff3cd;
  border-color: #ffeaa7;
}

.alert-secondary {
  color: #383d41;
  background-color: #e2e3e5;
  border-color: #d6d8db;
}

.small {
  font-size: 0.875rem;
}

.bx-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.form-select.is-invalid {
  border-color: #dc3545;
}

.form-select option:disabled {
  color: #6c757d !important;
  background-color: #e9ecef !important;
}

.d-block {
  display: block !important;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

.display-1 {
  font-size: 5rem;
}

.bg-danger {
  background-color: #dc3545 !important;
}

.text-white {
  color: #fff !important;
}

/* ✅ NEW: Purple color for dependency */
.text-purple {
  color: #6f42c1 !important;
}

.bg-purple {
  background-color: #6f42c1 !important;
}

.border-purple {
  border-color: #6f42c1 !important;
}

.bg-purple.bg-opacity-10 {
  background-color: rgba(111, 66, 193, 0.1) !important;
}

.bg-danger.bg-opacity-10 {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

/* ✅ NEW: Card styling for special cases */
.card.border-danger {
  border-color: #dc3545 !important;
}

.card.border-purple {
  border-color: #6f42c1 !important;
}

.card.border-2 {
  border-width: 2px !important;
}

.card-header.bg-danger.bg-opacity-10 {
  background-color: rgba(220, 53, 69, 0.1) !important;
  border-bottom: 1px solid rgba(220, 53, 69, 0.2);
}

.card-header.bg-purple.bg-opacity-10 {
  background-color: rgba(111, 66, 193, 0.1) !important;
  border-bottom: 1px solid rgba(111, 66, 193, 0.2);
}

/* ✅ NEW: Form check styling */
.form-check-input:checked {
  background-color: #2c5aa0;
  border-color: #2c5aa0;
}

.form-check-label {
  cursor: pointer;
}

.form-check-label i {
  vertical-align: middle;
}

/* ✅ NEW: Textarea styling */
textarea.form-control {
  resize: vertical;
  min-height: 60px;
}

textarea.form-control.is-invalid {
  border-color: #dc3545;
}

/* ✅ NEW: Transition for cards */
.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Horizontal rule styling */
hr {
  border-top: 1px dashed #dee2e6;
}

/* Alert adjustments */
.alert.py-2 {
  padding-top: 0.5rem !important;
  padding-bottom: 0.5rem !important;
}

/* Icon vertical alignment */
.bx {
  vertical-align: middle;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modal-dialog {
    margin: 0.5rem;
  }

  .card-body.py-2 {
    padding: 0.75rem !important;
  }

  .alert-info .d-flex {
    flex-direction: column;
  }

  .alert-info .d-flex i {
    margin-bottom: 0.5rem;
  }
}
</style>